/**
 * Keyboard validation composable for input components.
 * Provides utilities to validate keypresses based on input type.
 */

/**
 * Checks if a key is a control key (navigation, editing, etc.)
 * These keys are always allowed regardless of input type.
 */
const CONTROL_KEYS = new Set([
  "Backspace",
  "Delete",
  "Tab",
  "Escape",
  "Enter",
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
  "Home",
  "End",
]);

export const isControlKey = (e: KeyboardEvent): boolean => {
  if (CONTROL_KEYS.has(e.key)) {
    return true;
  }

  // Allow Ctrl/Cmd shortcuts (copy, paste, select all, etc.)
  if (e.ctrlKey || e.metaKey) {
    return true;
  }

  return false;
};

/**
 * Validates if a key press should be allowed for number input.
 * Allows: digits, minus, decimal point, and control keys
 */
export const isValidNumberKey = (e: KeyboardEvent): boolean => {
  const key = e.key;

  // Allow control keys
  if (isControlKey(e)) {
    return true;
  }

  // Allow digits
  if (/^\d$/.test(key)) {
    return true;
  }

  // Allow minus (only at start)
  if (key === "-") {
    const input = e.target as HTMLInputElement;
    return input.selectionStart === 0;
  }

  // Allow decimal point (only one)
  if (key === "." || key === ",") {
    const input = e.target as HTMLInputElement;
    return !input.value.includes(".") && !input.value.includes(",");
  }

  return false;
};

/**
 * Validates if a key press should be allowed based on input type.
 * Used by InputOtp for validating numeric or alphanumeric input.
 *
 * @param e - Keyboard event
 * @param type - 'number' for digits only, 'text' for alphanumeric
 * @returns true if key is valid
 */
/**
 * Predict the input value after a digit keystroke and clamp to min/max bounds.
 * Prevents the keystroke and sets the clamped value when it would exceed bounds.
 *
 * @returns true if the event was handled (clamped), false if no action was needed.
 */
export const clampNumberKeypress = (e: KeyboardEvent, min: number | undefined, max: number | undefined): boolean => {
  if (!/^\d$/.test(e.key)) return false;

  const input = e.target as HTMLInputElement;
  const start = input.selectionStart ?? 0;
  const end = input.selectionEnd ?? 0;
  const predicted = Number.parseFloat(input.value.slice(0, start) + e.key + input.value.slice(end));

  if (Number.isNaN(predicted)) return false;

  if (max !== undefined && predicted > max) {
    e.preventDefault();
    input.value = String(max);
    return true;
  }
  if (min !== undefined && predicted < min) {
    e.preventDefault();
    input.value = String(min);
    return true;
  }

  return false;
};

export const isValidInputKey = (e: KeyboardEvent, type: "number" | "text"): boolean => {
  // Allow control keys
  if (isControlKey(e)) {
    return true;
  }

  // For single character keys, validate based on type
  if (e.key.length === 1) {
    return type === "number" ? /^\d$/.test(e.key) : /^[a-z\d]$/i.test(e.key);
  }

  return false;
};
